import {
    REIMBURSEMENT_PROOF_MAX_COUNT,
    REIMBURSEMENT_PROOF_MIME_TYPES,
} from '@houzy/schemas/partner-cockpit/reimbursement';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, useRef, type ChangeEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useUploadReimbursementProof } from '../api/reimbursementApi';
import { useReimbursementDraft } from '../state/reimbursementDraftContext';

export function ReimbursementProofUpload(): ReactElement {
    const { t } = useTranslation();
    const labelId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const { draft, updateDraft, offerRequestLeadUuid } = useReimbursementDraft();
    const upload = useUploadReimbursementProof(offerRequestLeadUuid);

    const documents = draft.proofDocuments;
    const isFull = documents.length >= REIMBURSEMENT_PROOF_MAX_COUNT;

    function handleFiles(event: ChangeEvent<HTMLInputElement>): void {
        const file = event.target.files?.[0];
        // Clear the input so picking the same file twice still fires a change event.
        event.target.value = '';
        if (!file || isFull) {
            return;
        }
        upload.mutate(file, {
            onSuccess: (document) => updateDraft({ proofDocuments: [...documents, document] }),
        });
    }

    return (
        <Stack spacing={1}>
            <FormLabel id={labelId}>{t('REIMBURSEMENT.PROOF.LABEL')}</FormLabel>
            <Typography variant="body2" color="text.secondary">
                {t('REIMBURSEMENT.PROOF.HINT')}
            </Typography>

            {documents.length > 0 && (
                <List dense aria-labelledby={labelId}>
                    {documents.map((document) => (
                        <ListItem
                            key={document.uuid}
                            secondaryAction={
                                <IconButton
                                    edge="end"
                                    aria-label={t('REIMBURSEMENT.PROOF.REMOVE', { name: document.displayName })}
                                    onClick={() =>
                                        updateDraft({
                                            proofDocuments: documents.filter((item) => item.uuid !== document.uuid),
                                        })
                                    }>
                                    <DeleteOutlineIcon />
                                </IconButton>
                            }>
                            <ListItemText primary={document.displayName} />
                        </ListItem>
                    ))}
                </List>
            )}

            <input
                ref={inputRef}
                type="file"
                hidden
                accept={REIMBURSEMENT_PROOF_MIME_TYPES.join(',')}
                onChange={handleFiles}
            />
            <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={isFull || upload.isPending}
                onClick={() => inputRef.current?.click()}
                sx={{ alignSelf: 'flex-start' }}>
                {upload.isPending ? t('REIMBURSEMENT.PROOF.UPLOADING') : t('REIMBURSEMENT.PROOF.ADD')}
            </Button>

            {isFull && <Alert severity="info">{t('REIMBURSEMENT.PROOF.LIMIT_REACHED')}</Alert>}
            {upload.isError && <Alert severity="error">{t('REIMBURSEMENT.PROOF.UPLOAD_ERROR')}</Alert>}
        </Stack>
    );
}
